import { useRef, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormInput } from '@/components/FormInput';
import { Button } from '@/components/Button';
import { useTranslation } from 'react-i18next';
import { useResetPasswordMutation } from '@/models/auth';

type FormValues = {
  token: string;
  password: string;
  passwordRepeat: string;
};

export const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [halt, setHalt] = useState(false);
  const [resetPassword, { isError, isSuccess, error }] =
    useResetPasswordMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      token: token ? token : undefined,
      password: '',
      passwordRepeat: '',
    },
  });

  const password = useRef({});
  password.current = watch('password', '');

  const onSubmit: SubmitHandler<FormValues> = async ({
    token,
    password,
    passwordRepeat,
  }) => {
    try {
      await resetPassword({ token, password, passwordRepeat });
    } catch (err: any) {
      console.log({ err });
    }
  };

  useMemo(() => {
    if (isError) {
      setHalt(true);
    }
    if (isError === false) {
      setHalt(false);
    }

    if (isSuccess) {
      navigate('/reset-success');
    }
  }, [isError, isSuccess]);

  return (
    <div className="p-7 w-96 flex flex-col items-center">
      <span className="mb-6 text-xl">{t('label.reset_password')}</span>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full flex justify-center flex-col items-center"
      >
        {halt && (
          <span className="text-sm mb-6 text-red-500">
            {(error as any)?.data?.message}
          </span>
        )}
        <FormInput
          name="password"
          label={t('label.password')}
          type="password"
          control={control}
          rules={{
            required: t('validation.empty'),
            minLength: {
              value: 5,
              message: t('auth.register.validation.password_min_length'),
            },
          }}
          error={errors.password}
          variant="outlined"
          fullWidth
          sx={{
            mb: '15px',
          }}
        />
        <FormInput
          name="passwordRepeat"
          label={t('auth.register.password_repeat')}
          type="password"
          control={control}
          rules={{
            required: t('validation.empty'),
            validate: (value: string) =>
              value === password.current ||
              t('auth.register.validation.passwords_not_match'),
          }}
          error={errors.passwordRepeat}
          variant="outlined"
          fullWidth
          sx={{
            mb: '15px',
          }}
        />

        <Button variant="contained" fullWidth type="submit">
          {t('label.reset')}
        </Button>
      </form>
    </div>
  );
};
